import type { Product } from '@noconflict/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../shared/api/supabase';
import { discardDraftProject, listProjects, moveProject, startOrder } from './api';

export const projectKeys = {
  all: ['projects'] as const,
  list: (workspaceId: string) => ['projects', workspaceId] as const,
};

export function useProjects(workspaceId: string) {
  return useQuery({
    queryKey: projectKeys.list(workspaceId),
    queryFn: () => listProjects(supabase, workspaceId),
  });
}

/** One project of a workspace, read from the same cached list the workspace page uses; null when absent. */
export function useProject(workspaceId: string, projectId: string, enabled = true) {
  return useQuery({
    queryKey: projectKeys.list(workspaceId),
    queryFn: () => listProjects(supabase, workspaceId),
    enabled,
    select: (projects) => projects.find((project) => project.id === projectId) ?? null,
  });
}

export function useStartOrder(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (product: Product) => startOrder(supabase, workspaceId, product),
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.list(workspaceId) }),
  });
}

/** Discarding removes the project, its order and whatever it had stored, so both prefixes are refreshed. */
export function useDiscardDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: string) => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('forbidden');
      await discardDraftProject(token, projectId);
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: projectKeys.all }),
        qc.invalidateQueries({ queryKey: ['order'] }),
      ]),
  });
}

export function useMoveProject(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) => moveProject(supabase, projectId, workspaceId),
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.all }),
  });
}
